import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  Printer,
  Wallet,
} from 'lucide-react';

import { useAuth } from '@/nucleo/auth/useAuth';
import { ehSoDre } from '@/nucleo/navegacao/apresentacao';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import { DADOS_RESUMO_APROVACAO } from './dados';
import { exportarResumoAprovacaoParaPdf, exportarResumoAprovacaoParaXlsx } from './exportar';

/**
 * `/operacao/financeiro/resumo-aprovacao-sme` — conferência institucional dos
 * contratos a serem enviados à SME antes da aprovação, no formato equivalente
 * à planilha física hoje usada para esse fim: uma linha por medição, com DRE,
 * tipo, processo SEI, valor, período e situação.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. Lido de `dados.ts` (que monta as
 * datas relativas a hoje): troque `DADOS_RESUMO_APROVACAO` e o `EstadoSimulado`
 * pela leitura da API, no mesmo `dados.ts`, quando o módulo nascer na API.
 *
 * Exportação em PDF (`jspdf`+`jspdf-autotable`) e XLSX (`xlsx`), mesmo padrão de
 * `portal-credenciado/extrato/exportar.ts`, as duas bibliotecas já são dependência
 * do projeto (Termos e Extrato as usam), não é biblioteca nova. "Imprimir"
 * (`window.print()`) continua ao lado, para quem prefere imprimir direto.
 *
 * A competência aceita `:competencia` na rota (`/sme/resumo-aprovacao/:competencia`
 * e `/operacao/financeiro/resumo-aprovacao-sme/:competencia`) e `?mes=` na URL, mesmo padrão do protótipo
 * (`ResumoAprovacaoSmePage.tsx`, `parseMesParam`). Sem nenhum dos dois, o
 * padrão é a competência mais recente presente no mock, não o mês corrente
 * do relógio — o dado simulado cobre só duas competências, as de dois e três
 * meses atrás, e abrir no mês corrente mostraria a tela vazia por padrão, sem
 * nenhum filtro alterado.
 */

type TipoContratado = 'PF' | 'PJ';
type StatusLote = 'RASCUNHO' | 'PRONTO_PARA_ENVIO' | 'APROVADO' | 'DEVOLVIDA';

interface LinhaResumoAprovacao {
  id: string;
  contrato: string;
  dreId: string;
  dreCodigo: string;
  dreNome: string;
  tipo: TipoContratado;
  tipoLabel: string;
  processoSei: string | null;
  valor: number;
  competencia: string;
  periodoInicio: string;
  periodoFim: string;
  atestePrevisto: string;
  credenciadoNome: string;
  statusLote: StatusLote;
}

const STATUS_TONE: Record<StatusLote, { variante: StatusVariant; rotulo: string }> = {
  APROVADO: { variante: 'aprovado', rotulo: 'Aprovado' },
  PRONTO_PARA_ENVIO: { variante: 'pendente', rotulo: 'Pronto p/ envio' },
  RASCUNHO: { variante: 'rascunho', rotulo: 'Em rascunho' },
  DEVOLVIDA: { variante: 'rejeitado', rotulo: 'Devolvido' },
};

function competenciaAtual(): string {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  return `${ano}-${mes}`;
}

/** Competência mais recente presente no mock — padrão de abertura da tela. */
function competenciaMaisRecente(dados: readonly LinhaResumoAprovacao[]): string {
  return dados.reduce(
    (maisRecente, linha) => (linha.competencia > maisRecente ? linha.competencia : maisRecente),
    dados[0]?.competencia ?? competenciaAtual(),
  );
}

/** Aceita "YYYY-MM", "YYYY-MM-01" ou "YYYY-MM-01__PF" — mesma tolerância do protótipo. */
function normalizarCompetencia(bruta?: string | null): string | null {
  if (!bruta) return null;
  const m = bruta.match(/^(\d{4}-\d{2})/);
  return m?.[1] ?? null;
}

function formatarDataCurta(iso: string): string {
  const data = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(data.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(data);
}

const COLUNAS: DataTableColumn<LinhaResumoAprovacao>[] = [
  { header: 'Contrato', accessorKey: 'contrato', cellClassName: 'font-mono text-xs' },
  { header: 'DRE', accessorKey: 'dreCodigo' },
  {
    header: 'Tipo',
    cell: (linha) => <Badge variant="outline">{linha.tipoLabel}</Badge>,
  },
  { header: 'Processo SEI', accessorKey: 'processoSei', cellClassName: 'font-mono text-xs' },
  {
    header: 'Valor',
    cell: (linha) => <MoneyDisplay value={linha.valor} />,
    cellClassName: 'text-right',
    headerClassName: 'text-right',
  },
  {
    header: 'Período',
    cell: (linha) => (
      <span className="text-xs">
        {formatarDataCurta(linha.periodoInicio)} a {formatarDataCurta(linha.periodoFim)}
      </span>
    ),
    cellClassName: 'text-center',
    headerClassName: 'text-center',
  },
  {
    header: 'Ateste',
    cell: (linha) => <span className="text-xs">{formatarDataCurta(linha.atestePrevisto)}</span>,
    cellClassName: 'text-center',
    headerClassName: 'text-center',
  },
  { header: 'Contratado / Empresa', accessorKey: 'credenciadoNome' },
  {
    header: 'Status',
    cell: (linha) => {
      const tom = STATUS_TONE[linha.statusLote];
      return <StatusBadge status={tom.variante} label={tom.rotulo} />;
    },
  },
];

/**
 * QUEM É SÓ DRE NÃO VÊ O RESUMO DE OUTRA DIRETORIA. Mesmo motivo de
 * `AprovacaoSmePage.tsx`: sem campo de DRE em `UsuarioAutenticado`, não há
 * como recortar `DADOS_SIMULADOS` pela diretoria de quem está olhando, e
 * mostrar o resumo de todas seria pior que não mostrar nenhum.
 */
export function ResumoAprovacaoSmePage() {
  const { usuario } = useAuth();

  if (ehSoDre(usuario?.papeis)) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Resumo para Aprovação"
          description="Conferência institucional dos contratos a serem enviados à SME."
        />
        <EmptyState
          icon={Building2}
          title="O resumo da sua diretoria ainda não está disponível"
          description="Mostrar só os contratos da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá o resumo não é exibido: mostrar o de todas as diretorias seria pior do que não mostrar nenhum."
        />
      </div>
    );
  }

  return <Conteudo />;
}

function Conteudo() {
  const navigate = useNavigate();
  const { competencia } = useParams();
  const [searchParams] = useSearchParams();
  const cenario = useCenarioDeTela();
  const dadosBase = useMemo(
    () => (cenario === 'sucesso' ? (DADOS_RESUMO_APROVACAO as LinhaResumoAprovacao[]) : []),
    [cenario],
  );

  const dresDisponiveis = useMemo(() => {
    const mapa = new Map<string, { codigo: string; nome: string }>();
    for (const linha of DADOS_RESUMO_APROVACAO as LinhaResumoAprovacao[]) {
      mapa.set(linha.dreId, { codigo: linha.dreCodigo, nome: linha.dreNome });
    }
    return Array.from(mapa.entries())
      .map(([value, { codigo, nome }]) => ({ value, label: nome, code: codigo }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, []);

  const [mes, setMes] = useState(
    () =>
      normalizarCompetencia(competencia ?? searchParams.get('mes')) ??
      competenciaMaisRecente(DADOS_RESUMO_APROVACAO as LinhaResumoAprovacao[]),
  );
  const [dreFiltro, setDreFiltro] = useState<string | null>(null);
  const [tipoFiltro, setTipoFiltro] = useState('ALL');

  const linhas = useMemo(() => {
    return dadosBase.filter((linha) => {
      if (mes && linha.competencia !== mes) return false;
      if (dreFiltro && linha.dreId !== dreFiltro) return false;
      if (tipoFiltro !== 'ALL' && linha.tipo !== tipoFiltro) return false;
      return true;
    });
  }, [dadosBase, mes, dreFiltro, tipoFiltro]);

  const totalValor = useMemo(() => linhas.reduce((soma, linha) => soma + linha.valor, 0), [linhas]);
  const totalDres = useMemo(() => new Set(linhas.map((linha) => linha.dreId)).size, [linhas]);

  const stats: StatItem[] = [
    { title: 'Contratos', value: linhas.length, icon: FileCheck2 },
    { title: 'DREs distintas', value: totalDres, icon: Building2 },
    {
      title: 'Valor total da competência',
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        totalValor,
      ),
      icon: Wallet,
      className: 'col-span-2',
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Resumo para Aprovação"
        description="Conferência institucional dos contratos a serem enviados à SME — formato equivalente à planilha física distribuída hoje. Exporte em PDF/XLSX, assine internamente e siga para o envio."
      />

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Voltar
        </Button>
      </div>

      <StatsGrid stats={stats} columns="grid-cols-2 md:grid-cols-4" />

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-4">
          <div>
            <Label htmlFor="resumo-mes">Competência</Label>
            <Input
              id="resumo-mes"
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="resumo-dre">DRE</Label>
            <SearchableSelect
              value={dreFiltro}
              onChange={setDreFiltro}
              options={dresDisponiveis}
              placeholder="Todas as DREs"
              searchPlaceholder="Buscar por código ou nome..."
              clearable
            />
          </div>
          <div>
            <Label htmlFor="resumo-tipo">Tipo</Label>
            <Select value={tipoFiltro} onValueChange={setTipoFiltro}>
              <SelectTrigger id="resumo-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos (CL + PJ)</SelectItem>
                <SelectItem value="PF">CL (Pessoa Física)</SelectItem>
                <SelectItem value="PJ">PJ</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              className="flex-1"
              disabled={linhas.length === 0}
              onClick={() => exportarResumoAprovacaoParaPdf(linhas, mes)}
            >
              <FileText className="mr-1 h-4 w-4" aria-hidden="true" /> PDF
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              disabled={linhas.length === 0}
              onClick={() => exportarResumoAprovacaoParaXlsx(linhas, mes)}
            >
              <FileSpreadsheet className="mr-1 h-4 w-4" aria-hidden="true" /> XLSX
            </Button>
            <Button variant="ghost" size="icon" onClick={() => window.print()} title="Imprimir">
              <Printer className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <EstadoSimulado
        cenario={cenario}
        descricao="o resumo para aprovação da SME"
        colunasDoEsqueleto={COLUNAS.length}
      >
        <DataTable
          columns={COLUNAS}
          data={linhas}
          emptyTitle="Nenhum contrato na competência"
          emptyDescription="Ajuste a competência, a DRE ou o tipo. Pode ser que as medições ainda não tenham sido geradas."
          ariaLabel="Resumo para aprovação SME"
        />
        {linhas.length > 0 && (
          // Emenda visualmente com o Card do DataTable (que já traz sua
          // própria borda arredondada) — equivalente ao `<tfoot>` do
          // protótipo, mas sem editar `ui/DataTable.tsx`, que é compartilhado
          // por telas fora desta.
          <Card className="-mt-4 rounded-t-none border-t-0">
            <CardContent className="flex items-center justify-between p-4 text-sm font-medium">
              <span>
                Total ({linhas.length} contrato{linhas.length === 1 ? '' : 's'})
              </span>
              <MoneyDisplay value={totalValor} />
            </CardContent>
          </Card>
        )}
      </EstadoSimulado>
    </div>
  );
}

export default ResumoAprovacaoSmePage;
